import { createContext, useContext, useEffect, useState, useMemo } from 'react'

const ThemeContext = createContext()

/**
 * Safely retrieves a value from localStorage.
 * Returns null if localStorage is unavailable (private browsing, storage blocked, etc.)
 * @param {string} key - The localStorage key to retrieve
 * @returns {string|null} The stored value or null on error
 */
function safeGetItem(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Safely stores a value in localStorage.
 * Silently fails if localStorage is unavailable (private browsing, quota exceeded, etc.)
 * @param {string} key - The localStorage key to set
 * @param {string} value - The value to store
 */
function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // localStorage unavailable (private browsing, quota exceeded, etc.)
  }
}

/**
 * Safely removes a value from localStorage.
 * Silently fails if localStorage is unavailable.
 * @param {string} key - The localStorage key to remove
 */
function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key)
  } catch {
    // localStorage unavailable
  }
}

/**
 * Provider component for the ThemeContext.
 * Manages the application theme (light/dark) and persists it to localStorage.
 * Handles migration from legacy 'ap-theme' key.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 */
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      // Migrate theme key from legacy 'ap-theme' to 'authentipet-theme'
      const legacy = safeGetItem('ap-theme')
      if (legacy) {
        safeSetItem('authentipet-theme', legacy)
        safeRemoveItem('ap-theme') // Clean up legacy key
        return legacy
      }
      return safeGetItem('authentipet-theme') || 'dark'
    }
    return 'dark'
  })

  useEffect(() => {
    const root = window.document.documentElement

    if (theme === 'light') {
      root.classList.remove('dark')
      root.classList.add('light')
    } else {
      root.classList.remove('light')
      root.classList.add('dark')
    }

    safeSetItem('authentipet-theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  const value = useMemo(() => ({ theme, toggleTheme }), [theme])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}